import React from 'react';
import { CheckCircle } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { usePatientFlow } from '../context/PatientFlowContext';

const PatientFlowProgress = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { flowState } = usePatientFlow();
  
  const steps = [
    { id: 1, label: 'Your Info', paths: ['/patient/onboarding', '/patient/intake-enhanced'], completed: flowState.intakeCompleted, target: '/patient/onboarding' },
    { id: 2, label: 'Find Clinics', paths: ['/search-v2', '/patient/clinic-search', '/find-clinics', '/search'], completed: flowState.clinicsSelected.length > 0, target: '/search-v2' },
    { id: 3, label: 'Compare Offers', paths: ['/patient/offers', '/patient/dashboard'], completed: !!flowState.selectedOffer, target: '/patient/dashboard' },
    { id: 4, label: 'Payment', paths: ['/patient/payment', '/patient/orders'], completed: flowState.paymentCompleted, target: '/patient/payment' },
  ];

  const getCurrentStepIndex = () => {
    const currentPath = location.pathname;
    for (let i = 0; i < steps.length; i++) {
      if (steps[i].paths.some(p => currentPath.includes(p.replace('/', '')))) {
        return i;
      }
    }
    return -1;
  };

  let currentIndex = getCurrentStepIndex();
  // POST-PAYMENT-TRUTH-01 — the current step came from the ROUTE alone, and the
  // dashboard is listed under "Compare Offers", so a patient who had already
  // paid a deposit was pushed back to step 3 just by opening the dashboard. A
  // collection that has happened is a fact about the journey, not a page.
  if (flowState.paymentCompleted && currentIndex >= 0 && currentIndex < 3) {
    currentIndex = 3;
  }
  if (currentIndex === -1) return null;

  // All steps are clickable — navigate preserves state (no reset)
  const handleStepClick = (step) => {
    navigate(step.target);
  };

  return (
    <div className="bg-white/95 backdrop-blur border-b shadow-sm py-2.5 sticky top-16 z-40" data-testid="patient-journey-progress">
      <div className="max-w-3xl mx-auto px-4">
        <div className="flex items-center justify-between gap-1">
          {steps.map((step, index) => (
            <React.Fragment key={step.id}>
              <button
                onClick={() => handleStepClick(step)}
                className="flex items-center gap-1.5 group cursor-pointer transition-opacity hover:opacity-80"
                data-testid={`journey-step-${step.id}`}
                aria-label={`Go to ${step.label}`}
              >
                <div className={`flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold transition-all ${
                  step.completed
                    ? 'bg-green-500 group-hover:bg-green-600'
                    : index === currentIndex
                    ? 'bg-blue-600 ring-2 ring-blue-200'
                    : index < currentIndex
                    ? 'bg-blue-400 group-hover:bg-blue-500'
                    : 'bg-gray-200 group-hover:bg-gray-300'
                }`}>
                  {step.completed ? (
                    <CheckCircle className="h-4 w-4 text-white" />
                  ) : (
                    <span className={
                      index === currentIndex ? 'text-white' 
                      : index < currentIndex ? 'text-white'
                      : 'text-gray-500'
                    }>
                      {step.id}
                    </span>
                  )}
                </div>
                <span className={`text-xs font-medium hidden sm:inline transition-colors ${
                  step.completed ? 'text-green-700 group-hover:text-green-800'
                    : index === currentIndex ? 'text-blue-700'
                    : index < currentIndex ? 'text-blue-500 group-hover:text-blue-600'
                    : 'text-gray-400 group-hover:text-gray-500'
                }`}>
                  {step.label}
                </span>
              </button>
              {index < steps.length - 1 && (
                <div className={`flex-1 h-0.5 mx-1 rounded ${
                  step.completed ? 'bg-green-400' 
                  : index < currentIndex ? 'bg-blue-300'
                  : 'bg-gray-200'
                }`} />
              )}
            </React.Fragment>
          ))}
        </div>
        <p className="text-[10px] text-center text-slate-400 mt-1">
          Step {currentIndex + 1} of {steps.length}
        </p>
      </div>
    </div>
  );
};

export default PatientFlowProgress;
